import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { KitHistoryEntry, ReceiptDTO } from "../../models/ArticleDTO";
import { requestErrorText } from "./kitUi";
import { abrirArchivo } from "../enrollment/visorArchivo";
import { fechaHoraAmPm } from "../../utils/fechaHoraAmPm";

export interface KitHistoryTimelineProps {
	campusId: number;
	/** Bitácora of one kit… */
	kitId?: number;
	/** …or of every kit of a student (newest first). */
	studentId?: number;
	/** Pre-loaded entries (skips the request). */
	entries?: KitHistoryEntry[];
	compact?: boolean;
	/** Permission section of the screen: "kits" (default: Control de Entregas, perfil del alumno) or "kitSales" (Control de Ventas). */
	section?: "kits" | "kitSales" | "newSale";
}

const TYPE_COLORS: Record<string, string> = {
	charged: "var(--wa-apagado)",
	advisors: "var(--wa-apagado)",
	payment: "var(--wa-tinta)",
	payment_cancelled: "var(--wa-oro)",
	cancelled: "var(--wa-oro)",
	reactivated: "var(--wa-acento)",
	delivery_partial: "var(--wa-oro)",
	delivery_total: "var(--wa-tinta)",
	delivery_status: "var(--wa-oro)",
	note: "var(--wa-apagado)"
};

const PILL = "btn btn-outline-secondary btn-sm rounded-pill";
const PILL_STYLE: React.CSSProperties = { padding: "1px 10px", fontSize: "12px", lineHeight: 1.5 };

/** Reason the WhatsApp did not go out when the backend gave none. */
const WA_FALLBACK: Record<string, string> = {
	omitido: "El alumno pidió no recibir WhatsApp.",
	sin_telefono: "El alumno no tiene teléfono.",
	sin_numero: "El plantel no tiene número de Caja conectado.",
	pendiente: "Pendiente de enviar.",
	error: "No se pudo enviar."
};

/**
 * Comprobante de Entrega of a delivery row (Oct 2026): opens the PDF, the signature and the photo in WAPP's own viewer
 * (files are behind the session, so they are fetched as blobs) and shows how the WhatsApp went; pendiente/error can be resent.
 */
const ReceiptRow: React.FC<{ campusId: number; receipt: ReceiptDTO; onUpdate: (receipt: ReceiptDTO) => void }> = ({ campusId, receipt, onUpdate }) => {
	const [sending, setSending] = useState<boolean>(false);
	const [sendError, setSendError] = useState<string | null>(null);
	const http = HttpManager.getInstance();

	const open = (kind: "pdf" | "firma" | "foto") => {
		const nombre = kind === "pdf" ? "Comprobante de entrega.pdf" : kind === "firma" ? "Firma del alumno.png" : "Foto de la entrega.jpg";
		abrirArchivo({ origen: "Kits", nombre, cargar: () => http.kitsService.getReceiptFile(campusId, receipt.id, kind) });
	};

	const resend = async () => {
		setSending(true);
		setSendError(null);
		try {
			const response = await http.kitsService.resendReceipt(campusId, receipt.id);
			if (response.data?.receipt) onUpdate(response.data.receipt);
		} catch (requestError) {
			setSendError(requestErrorText(requestError, "No se pudo reenviar."));
		} finally {
			setSending(false);
		}
	};

	const retryable = receipt.waStatus === "pendiente" || receipt.waStatus === "error";
	return <div style={{ marginTop: "6px" }}>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
			<button type="button" className={PILL} style={PILL_STYLE} onClick={() => open("pdf")}>Comprobante</button>
			<button type="button" className={PILL} style={PILL_STYLE} onClick={() => open("firma")}>Firma</button>
			{receipt.photoUrl ? <button type="button" className={PILL} style={PILL_STYLE} onClick={() => open("foto")}>Foto</button> : null}
		</div>
		<div style={{ display: "flex", gap: "6px 10px", flexWrap: "wrap", alignItems: "center", marginTop: "4px", fontSize: "12px" }}>
			{receipt.waStatus === "enviado"
				? <span style={{ color: "var(--wa-apagado)" }}>Enviado por WhatsApp de Caja{receipt.sentAt ? " · " + receipt.sentAt : ""}{receipt.phone ? " · " + receipt.phone : ""}</span>
				: <span style={{ color: retryable ? "var(--wa-oro)" : "var(--wa-apagado)" }}>{receipt.waMessage || WA_FALLBACK[receipt.waStatus] || receipt.waStatus}</span>}
			{retryable ? <button type="button" className={PILL} style={PILL_STYLE} disabled={sending} onClick={resend}>{sending ? "Enviando…" : "Reenviar"}</button> : null}
			{sendError ? <span style={{ color: "var(--wa-oro)" }}>{sendError}</span> : null}
		</div>
	</div>;
};

/**
 * Read-only "bitácora" of a kit (kits overhaul, Sep 2026): who did what, when, with which note.
 * Used inside the student's financial profile and the Kits module. Never edits anything.
 */
const KitHistoryTimeline: React.FC<KitHistoryTimelineProps> = ({ campusId, kitId, studentId, entries, compact, section }) => {
	const [rows, setRows] = useState<KitHistoryEntry[]>(entries || []);
	const [loading, setLoading] = useState<boolean>(!entries);
	const [error, setError] = useState<string | null>(null);
	const http = HttpManager.getInstance();

	useEffect(() => {
		if (entries) { setRows(entries); setLoading(false); return; }
		let alive = true;
		setLoading(true);
		setError(null);
		const request = kitId
			? http.kitsService.getHistory(campusId, kitId, section || "kits")
			: http.kitsService.getStudentHistory(campusId, studentId as number, section || "kits");
		request
			.then((response) => { if (alive) setRows(response.data || []); })
			.catch((requestError) => { if (alive) setError(requestErrorText(requestError, "No se pudo cargar el historial.")); })
			.finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [campusId, kitId, studentId, entries, section]);

	if (loading) return <p style={{ color: "var(--wa-apagado)", margin: 0 }}>Cargando historial…</p>;
	if (error) return <p style={{ color: "var(--wa-oro)", margin: 0 }}>{error}</p>;
	if (rows.length < 1) return <p style={{ color: "var(--wa-apagado)", margin: 0 }}>Sin movimientos registrados.</p>;

	return <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
		{rows.map((row) => <li key={row.id} style={{ display: "flex", gap: "10px", padding: compact ? "6px 0" : "10px 0", borderBottom: "1px solid var(--wa-filo)" }}>
			<span style={{ flex: "0 0 10px", marginTop: "6px", width: "10px", height: "10px", borderRadius: "50%", background: TYPE_COLORS[row.type] || "var(--wa-apagado)" }} />
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", alignItems: "baseline" }}>
					<b style={{ color: TYPE_COLORS[row.type] || "var(--wa-tinta)" }}>{row.typeLabel}</b>
					<span style={{ color: "var(--wa-apagado)", fontSize: "12px" }}>{fechaHoraAmPm(row.createdAt)}{row.author ? " · " + row.author : ""}</span>
					{row.kitTitle ? <span style={{ color: "var(--wa-apagado)", fontSize: "12px" }}>{row.kitTitle}{row.kitDescription ? " · " + row.kitDescription : ""}</span> : null}
				</div>
				<div style={{ fontSize: compact ? "13px" : "14px", wordBreak: "break-word" }}>{row.summary}</div>
				{row.note ? <div style={{ fontSize: "13px", color: "var(--wa-tinta)", background: "var(--wa-papel-2)", borderLeft: "3px solid var(--wa-filo-2)", padding: "4px 8px", marginTop: "4px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.note}</div> : null}
				{row.receipt ? <ReceiptRow campusId={campusId} receipt={row.receipt}
					onUpdate={(receipt) => setRows((previous) => previous.map((item) => item.id === row.id ? { ...item, receipt } : item))} /> : null}
			</div>
		</li>)}
	</ul>;
};

export default KitHistoryTimeline;
